import Text from "@/components/ui/text";
import React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  TooltipProps,
  PieLabelRenderProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { isEmpty } from "lodash";
import { DreamerPieChartDataType } from "@/components/DreamerCharts/types";
import LegendWithMore from "@/components/DreamerCharts/LegendWithMore";

interface Props {
  size: number;
  chartData: DreamerPieChartDataType[];
  centerLabel?: string;
  centerValue?: string;
  showLegend?: boolean;
  legendClassName?: string;
  innerRadius?: number;
  outerRadius?: number;
  compact?: boolean;
}

const PieTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DreamerPieChartDataType;
    return (
      <div className="bg-surface-elevation3 border border-border-border1 rounded-lg p-2 shadow-lg">
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: data.color }}
          />
          <Text variant="smBody" className="text-primary">
            {data.name}: {data.value}%
          </Text>
        </div>
      </div>
    );
  }
  return null;
};

const renderOutsideLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  value
}: PieLabelRenderProps) => {
  const RADIAN = Math.PI / 180;
  const cxNum = Number(cx);
  const cyNum = Number(cy);
  const outerRadiusNum = Number(outerRadius);
  const midAngleNum = Number(midAngle);
  const radius = outerRadiusNum + 15;
  const x = cxNum + radius * Math.cos(-midAngleNum * RADIAN);
  const y = cyNum + radius * Math.sin(-midAngleNum * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="var(--secondary)"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={400}
    >
      {`${value}%`}
    </text>
  );
};

const DreamerPieChart: React.FC<Props> = ({
  size,
  chartData,
  centerLabel,
  centerValue,
  showLegend = true,
  legendClassName,
  innerRadius = 65,
  outerRadius = 75,
  compact = false
}) => {
  if (isEmpty(chartData)) return null;

  const chartSize = size + 100;

  return (
    <div
      className={`flex flex-col items-center w-full ${
        compact ? "" : "h-full justify-center"
      }`}
    >
      <div style={{ width: chartSize, height: chartSize }} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={3}
              dataKey="value"
              labelLine={false}
              label={renderOutsideLabel}
              cornerRadius={10}
              strokeWidth={0}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            <RechartsTooltip content={<PieTooltip />} />
          </RechartsPieChart>
        </ResponsiveContainer>
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {centerValue && (
              <Text
                variant="lgLabel"
                className="text-primary text-xl font-semibold"
              >
                {centerValue}
              </Text>
            )}
            {centerLabel && (
              <Text variant="smBody" className="text-secondary">
                {centerLabel}
              </Text>
            )}
          </div>
        )}
      </div>
      {showLegend && (
        <LegendWithMore
          items={chartData.map((d, i) => ({
            key: `${d.name}-${i}`,
            label: d.name,
            color: d.color
          }))}
          className={legendClassName}
        />
      )}
    </div>
  );
};

export default DreamerPieChart;
